import { useEffect, useRef, useState } from 'react';

import type { Question, QuotaGroup } from '@api/surveys/utilities/types';

import { type Item, type PreviewSelection } from './helpers';
import { useStatsigClient } from '@statsig/react-bindings';
import { GATES } from '@constants';

interface PreviewSelectionList {
  questions?: Question[];
  quotaGroups?: QuotaGroup[];
}

/**
 * useState like helper for checking if the user can preview surveys
 * @returns [canPreviewSurvey]
 */
export const useCanPreviewSurveys = () => {
  const { client } = useStatsigClient();
  const canPreviewSurvey = client.checkGate(GATES.PREVIEW_SURVEY);
  return [canPreviewSurvey];
};

/**
 *  hook for consolidating the logic needed to render a survey preview
 */
export const useSurveyPreview = ({ questions, quotaGroups }: PreviewSelectionList) => {
  const {
    handleUpdatePreviewQuestion,
    handleUpdatePreviewQuotaGroup,
    previewSelection,
    resetPreviewQuestion,
    resetPreviewSelection,
    setPreviewSelection,
  } = usePreviewSelection({
    questionId: questions?.[0]?.id,
    quotaGroupId: quotaGroups?.[0]?.id,
  });
  const [showPreviewModal, handleClosePreviewModal, handlePreviewButtonClick] = useShowPreviewModal(
    {
      close: resetPreviewSelection,
      updateQuestion: handleUpdatePreviewQuestion,
    }
  );
  useValidPreviewSelection({
    questions,
    quotaGroups,
    setPreviewSelection,
  });
  return {
    handleClosePreviewModal,
    handlePreviewButtonClick,
    handleUpdatePreviewQuestion,
    handleUpdatePreviewQuotaGroup,
    previewSelection,
    resetPreviewQuestion,
    showPreviewModal,
  };
};

/**
 * hook for managing the preview selection state
 */
const usePreviewSelection = ({ questionId, quotaGroupId }: PreviewSelection) => {
  const latestInitialPreviewSelectionRef = useRef({ questionId, quotaGroupId });
  const [previewSelection, setPreviewSelection] = useState(
    latestInitialPreviewSelectionRef.current
  );

  useEffect(() => {
    latestInitialPreviewSelectionRef.current = { questionId, quotaGroupId };
  }, [questionId, quotaGroupId]);

  const handleUpdatePreviewQuotaGroup = (quotaGroupId: number) => {
    setPreviewSelection((prev) => {
      return {
        ...prev,
        quotaGroupId,
      };
    });
  };

  const handleUpdatePreviewQuestion = (questionId: number) => {
    setPreviewSelection((prev) => {
      return {
        ...prev,
        questionId,
      };
    });
  };

  const resetPreviewQuestion = () => {
    const nextQuestionId = latestInitialPreviewSelectionRef.current.questionId;
    if (nextQuestionId === undefined) return;

    handleUpdatePreviewQuestion(nextQuestionId);
  };

  const resetPreviewSelection = () => setPreviewSelection(latestInitialPreviewSelectionRef.current);

  return {
    handleUpdatePreviewQuestion,
    handleUpdatePreviewQuotaGroup,
    previewSelection,
    resetPreviewQuestion,
    resetPreviewSelection,
    setPreviewSelection,
  };
};

/**
 * hook for interacting with the preview button and closing the preview modal
 */
const useShowPreviewModal = ({
  close,
  updateQuestion,
}: {
  close: () => void;
  updateQuestion: (questionId: number) => void;
}) => {
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false);
    close();
  };

  const handlePreviewButtonClick = (questionId?: number) => {
    if (typeof questionId === 'number') updateQuestion(questionId);
    setShowPreviewModal(true);
  };

  return [showPreviewModal, handleClosePreviewModal, handlePreviewButtonClick];
};

/**
 * hook for ensuring that the preview selection is always valid
 */
const useValidPreviewSelection = ({
  questions,
  quotaGroups,
  setPreviewSelection,
}: PreviewSelectionList & {
  setPreviewSelection: React.Dispatch<
    React.SetStateAction<{
      questionId: number | undefined;
      quotaGroupId: number | undefined;
    }>
  >;
}) => {
  useEffect(() => {
    if (!questions || !quotaGroups) return;

    setPreviewSelection((prev) => {
      if (!prev) {
        return {
          questionId: questions?.[0]?.id,
          quotaGroupId: quotaGroups?.[0]?.id,
        };
      }

      const { questionId, quotaGroupId } = prev;
      const questionIsValid = checkIfValid(questions, questionId);
      const quotaGroupIsValid = checkIfValid(quotaGroups, quotaGroupId);
      if (questionIsValid && quotaGroupIsValid) return prev;

      return {
        questionId: questionIsValid ? questionId : questions[0]?.id,
        quotaGroupId: quotaGroupIsValid ? quotaGroupId : quotaGroups[0]?.id,
      };
    });
  }, [questions, quotaGroups, setPreviewSelection]);
};

const checkIfValid = (items: Item[], id?: number) => {
  if (id === undefined || id === null) return false;
  return items.some((item) => String(item.id) === String(id));
};
